import React, { useEffect, useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaSearch, FaSortAmountDown, FaSortAmountUp, FaBuilding, FaPercentage, FaLayerGroup, FaTimes, FaCalendarAlt, FaTruck } from 'react-icons/fa';
import { toast } from 'react-toastify';
import {
    fetchRetailerDistributorDetail,
    fetchRetailerDistributorDiscounts,
} from '../../../services/Http/Api';
import PageContainer from '../../../shared/layout/containers/PageContainer';
import PageContent from '../../../shared/layout/containers/PageContent';
import { useCurrency } from '../../../context/CurrencyProvider';
import { formatDiscountPercent, resolveDiscountedAmounts } from '../../../utils/discountDisplay';

const RetailerDistributorDetail = () => {
    const { t } = useTranslation();
    const { id: distributorId } = useParams();
    const navigate = useNavigate();
    const { formatResolvedAmount } = useCurrency();

    // Distributor info state
    const [distributor, setDistributor] = useState(null);
    const [metrics, setMetrics] = useState(null);
    const [manufacturers, setManufacturers] = useState([]);
    const [loading, setLoading] = useState(true);

    // Active tab
    const [activeTab, setActiveTab] = useState('discounts');

    // Discounts state
    const [discounts, setDiscounts] = useState([]);
    const [discountsLoading, setDiscountsLoading] = useState(true);
    const [discountsMeta, setDiscountsMeta] = useState({
        current_page: 1,
        per_page: 20,
        total: 0,
        last_page: 1,
    });
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [sortBy, setSortBy] = useState('discount_value');
    const [sortDirection, setSortDirection] = useState('desc');

    // Detail modal state
    const [selectedDiscount, setSelectedDiscount] = useState(null);

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search);
        }, 300);
        return () => clearTimeout(timer);
    }, [search]);

    // Load distributor detail
    useEffect(() => {
        const loadDistributor = async () => {
            setLoading(true);
            try {
                const data = await fetchRetailerDistributorDetail(distributorId);
                setDistributor(data.distributor);
                setMetrics(data.metrics);
                setManufacturers(data.manufacturers || []);
            } catch (error) {
                toast.error(t('RetailerDistributorDetail.error'));
                navigate('/distributors');
            } finally {
                setLoading(false);
            }
        };
        loadDistributor();
    }, [distributorId, navigate, t]);

    // Load discounts
    const loadDiscounts = useCallback(async (page = 1) => {
        setDiscountsLoading(true);
        try {
            const params = {
                page,
                per_page: discountsMeta.per_page,
                sort_by: sortBy,
                sort_direction: sortDirection,
            };
            if (debouncedSearch) {
                params.search = debouncedSearch;
            }
            const response = await fetchRetailerDistributorDiscounts(distributorId, params);
            setDiscounts(response.data || []);
            setDiscountsMeta(response.meta || discountsMeta);
        } catch (error) {
            console.error('Error loading discounts:', error);
        } finally {
            setDiscountsLoading(false);
        }
    }, [distributorId, debouncedSearch, sortBy, sortDirection, discountsMeta.per_page]);

    // Reload discounts when filters change
    useEffect(() => {
        if (activeTab === 'discounts') {
            loadDiscounts(1);
        }
    }, [debouncedSearch, sortBy, sortDirection, activeTab, loadDiscounts]);

    const handlePageChange = (page) => {
        if (page < 1 || page > discountsMeta.last_page) return;
        loadDiscounts(page);
    };

    const handleSort = (field) => {
        if (sortBy === field) {
            setSortDirection(sortDirection === 'desc' ? 'asc' : 'desc');
        } else {
            setSortBy(field);
            setSortDirection('desc');
        }
    };

    const renderPagination = () => {
        if (discountsMeta.last_page <= 1) return null;

        const pages = [];
        const showPages = 5;
        let startPage = Math.max(1, discountsMeta.current_page - Math.floor(showPages / 2));
        let endPage = Math.min(discountsMeta.last_page, startPage + showPages - 1);

        if (endPage - startPage + 1 < showPages) {
            startPage = Math.max(1, endPage - showPages + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }

        return (
            <Pagination>
                <PageButton
                    onClick={() => handlePageChange(discountsMeta.current_page - 1)}
                    disabled={discountsMeta.current_page === 1}
                >
                    {t('common.previous')}
                </PageButton>
                {pages.map(page => (
                    <PageNumber
                        key={page}
                        $active={page === discountsMeta.current_page}
                        onClick={() => handlePageChange(page)}
                    >
                        {page}
                    </PageNumber>
                ))}
                <PageButton
                    onClick={() => handlePageChange(discountsMeta.current_page + 1)}
                    disabled={discountsMeta.current_page === discountsMeta.last_page}
                >
                    {t('common.next')}
                </PageButton>
            </Pagination>
        );
    };

    if (loading) {
        return (
            <PageContainer>
                <PageContent>
                    <LoadingPlaceholder>{t('common.loading')}</LoadingPlaceholder>
                </PageContent>
            </PageContainer>
        );
    }

    return (
        <PageContainer>
            <PageContent>
                {/* Back Button and Header */}
                <BackButton onClick={() => navigate('/distributors')}>
                    <FaArrowLeft size={14} />
                    {t('RetailerDistributorDetail.backToList')}
                </BackButton>

                <Header>
                    <HeaderInfo>
                        {distributor?.logo ? (
                            <DistributorLogo src={distributor.logo} alt={distributor.name} />
                        ) : (
                            <LogoPlaceholder>{distributor?.name?.charAt(0)}</LogoPlaceholder>
                        )}
                        <HeaderText>
                            <DistributorName>{distributor?.name}</DistributorName>
                            {distributor?.description && (
                                <DistributorDescription>{distributor.description}</DistributorDescription>
                            )}
                        </HeaderText>
                    </HeaderInfo>
                </Header>

                {/* Metrics */}
                <MetricsGrid>
                    <MetricCard>
                        <MetricIcon><FaBuilding size={20} /></MetricIcon>
                        <MetricValue>{metrics?.manufacturers_count || 0}</MetricValue>
                        <MetricLabel>{t('RetailerDistributorDetail.manufacturers')}</MetricLabel>
                    </MetricCard>
                    <MetricCard>
                        <MetricIcon><FaPercentage size={20} /></MetricIcon>
                        <MetricValue>{metrics?.active_discounts_count || 0}</MetricValue>
                        <MetricLabel>{t('RetailerDistributorDetail.activeDiscounts')}</MetricLabel>
                    </MetricCard>
                    <MetricCard>
                        <MetricIcon><FaLayerGroup size={20} /></MetricIcon>
                        <MetricValue>{metrics?.categories?.length || 0}</MetricValue>
                        <MetricLabel>{t('RetailerDistributorDetail.categories')}</MetricLabel>
                    </MetricCard>
                </MetricsGrid>

                {/* Tabs */}
                <TabsContainer>
                    <TabButton
                        $active={activeTab === 'discounts'}
                        onClick={() => setActiveTab('discounts')}
                    >
                        {t('RetailerDistributorDetail.discountsTab')}
                    </TabButton>
                    <TabButton
                        $active={activeTab === 'manufacturers'}
                        onClick={() => setActiveTab('manufacturers')}
                    >
                        {t('RetailerDistributorDetail.manufacturersTab')}
                    </TabButton>
                    <TabButton
                        $active={activeTab === 'info'}
                        onClick={() => setActiveTab('info')}
                    >
                        {t('RetailerDistributorDetail.infoTab')}
                    </TabButton>
                </TabsContainer>

                {/* Tab Content */}
                {activeTab === 'discounts' && (
                    <>
                        <FiltersContainer>
                            <SearchWrapper>
                                <SearchIcon><FaSearch size={14} /></SearchIcon>
                                <SearchInput
                                    type="text"
                                    placeholder={t('RetailerDistributorDetail.searchDiscounts')}
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                            </SearchWrapper>
                            <SortContainer>
                                <SortLabel>{t('RetailerBestOffers.sortBy')}:</SortLabel>
                                <SortButton
                                    $active={sortBy === 'discount_value'}
                                    onClick={() => handleSort('discount_value')}
                                >
                                    {t('RetailerBestOffers.sortDiscount')}
                                    {sortBy === 'discount_value' && (
                                        sortDirection === 'desc' ? <FaSortAmountDown size={12} /> : <FaSortAmountUp size={12} />
                                    )}
                                </SortButton>
                                <SortButton
                                    $active={sortBy === 'end_at'}
                                    onClick={() => handleSort('end_at')}
                                >
                                    {t('RetailerBestOffers.sortDate')}
                                    {sortBy === 'end_at' && (
                                        sortDirection === 'desc' ? <FaSortAmountDown size={12} /> : <FaSortAmountUp size={12} />
                                    )}
                                </SortButton>
                            </SortContainer>
                        </FiltersContainer>

                        {discountsLoading ? (
                            <LoadingGrid>
                                {[...Array(4)].map((_, i) => (
                                    <SkeletonCard key={i}>
                                        <SkeletonImage />
                                        <SkeletonContent>
                                            <SkeletonLine $width="80%" />
                                            <SkeletonLine $width="60%" />
                                        </SkeletonContent>
                                    </SkeletonCard>
                                ))}
                            </LoadingGrid>
                        ) : discounts.length === 0 ? (
                            <Placeholder>{t('RetailerDistributorDetail.noDiscounts')}</Placeholder>
                        ) : (
                            <>
                                <DiscountsGrid>
                                    {discounts.map((discount) => {
                                        const discountPercent = formatDiscountPercent(discount.discount_percentage);
                                        const { eurAmount, localAmount } = resolveDiscountedAmounts(discount);

                                        const formattedDiscountedPrice = eurAmount !== null
                                            ? formatResolvedAmount({
                                                eurAmount,
                                                localAmount,
                                                localCurrencyId: discount.initial_price_currency_id,
                                            })
                                            : null;

                                        const formattedInitialPrice = discount.initial_price
                                            ? formatResolvedAmount({
                                                eurAmount: discount.initial_price,
                                                localAmount: discount.initial_price_local,
                                                localCurrencyId: discount.initial_price_currency_id,
                                            })
                                            : null;

                                        return (
                                            <DiscountCard key={discount.id} onClick={() => setSelectedDiscount(discount)}>
                                                {discountPercent && <DiscountBadge>-{discountPercent}</DiscountBadge>}
                                                <CardImageWrapper>
                                                    {discount.image_url ? (
                                                        <ProductImage src={discount.image_url} alt={discount.product_name} />
                                                    ) : (
                                                        <ImagePlaceholder>{discount.product_name.charAt(0)}</ImagePlaceholder>
                                                    )}
                                                </CardImageWrapper>
                                                <CardContent>
                                                    <ProductName title={discount.product_name}>
                                                        {truncate(discount.product_name, 35)}
                                                    </ProductName>
                                                    {discount.manufacturer_name && (
                                                        <ManufacturerTag>{discount.manufacturer_name}</ManufacturerTag>
                                                    )}
                                                    <CardFooter>
                                                        {formattedDiscountedPrice && formattedInitialPrice ? (
                                                            <PriceSection>
                                                                <NewPrice>{formattedDiscountedPrice}</NewPrice>
                                                                <OldPrice>{formattedInitialPrice}</OldPrice>
                                                            </PriceSection>
                                                        ) : (
                                                            <DiscountOnly>
                                                                {formatResolvedAmount({
                                                                    eurAmount: discount.discount_value,
                                                                    localAmount: null,
                                                                    localCurrencyId: discount.initial_price_currency_id,
                                                                })} off
                                                            </DiscountOnly>
                                                        )}
                                                    </CardFooter>
                                                </CardContent>
                                            </DiscountCard>
                                        );
                                    })}
                                </DiscountsGrid>
                                {renderPagination()}
                            </>
                        )}
                    </>
                )}

                {activeTab === 'manufacturers' && (
                    <ManufacturersGrid>
                        {manufacturers.length === 0 ? (
                            <Placeholder>{t('RetailerDistributorDetail.noManufacturers')}</Placeholder>
                        ) : (
                            manufacturers.map((manufacturer) => (
                                <ManufacturerCard key={manufacturer.id}>
                                    <ManufacturerLogo>{manufacturer.name.charAt(0)}</ManufacturerLogo>
                                    <ManufacturerInfo>
                                        <ManufacturerTitle>{manufacturer.name}</ManufacturerTitle>
                                        <ManufacturerCategory>{manufacturer.category}</ManufacturerCategory>
                                    </ManufacturerInfo>
                                </ManufacturerCard>
                            ))
                        )}
                    </ManufacturersGrid>
                )}

                {activeTab === 'info' && (
                    <InfoSection>
                        <InfoItem>
                            <InfoLabel>{t('RetailerDistributorDetail.name')}</InfoLabel>
                            <InfoValue>{distributor?.name || '-'}</InfoValue>
                        </InfoItem>
                        <InfoItem>
                            <InfoLabel>{t('RetailerDistributorDetail.description')}</InfoLabel>
                            <InfoValue>{distributor?.description || '-'}</InfoValue>
                        </InfoItem>
                        {distributor?.email && (
                            <InfoItem>
                                <InfoLabel>{t('RetailerDistributorDetail.email')}</InfoLabel>
                                <InfoValue>{distributor.email}</InfoValue>
                            </InfoItem>
                        )}
                        {distributor?.website && (
                            <InfoItem>
                                <InfoLabel>{t('RetailerDistributorDetail.website')}</InfoLabel>
                                <InfoValue>
                                    <a href={distributor.website} target="_blank" rel="noopener noreferrer">
                                        {distributor.website}
                                    </a>
                                </InfoValue>
                            </InfoItem>
                        )}
                        {metrics?.categories?.length > 0 && (
                            <InfoItem>
                                <InfoLabel>{t('RetailerDistributorDetail.categoriesList')}</InfoLabel>
                                <CategoriesList>
                                    {metrics.categories.map((cat, index) => (
                                        <CategoryTag key={index}>{cat}</CategoryTag>
                                    ))}
                                </CategoriesList>
                            </InfoItem>
                        )}
                    </InfoSection>
                )}

                {/* Detail Modal */}
                {selectedDiscount && (
                    <ModalOverlay onClick={() => setSelectedDiscount(null)}>
                        <ModalContainer onClick={(e) => e.stopPropagation()}>
                            <ModalCloseButton onClick={() => setSelectedDiscount(null)}>
                                <FaTimes size={16} />
                            </ModalCloseButton>
                            <ModalImageSection>
                                {selectedDiscount.image_url ? (
                                    <ModalImage src={selectedDiscount.image_url} alt={selectedDiscount.product_name} />
                                ) : (
                                    <ModalImagePlaceholder>
                                        {selectedDiscount.product_name.charAt(0)}
                                    </ModalImagePlaceholder>
                                )}
                                {formatDiscountPercent(selectedDiscount.discount_percentage) && (
                                    <ModalDiscountBadge>
                                        -{formatDiscountPercent(selectedDiscount.discount_percentage)}
                                    </ModalDiscountBadge>
                                )}
                            </ModalImageSection>
                            <ModalBody>
                                <ModalProductName>{selectedDiscount.product_name}</ModalProductName>
                                {selectedDiscount.description && (
                                    <ModalDescription>{selectedDiscount.description}</ModalDescription>
                                )}
                                <ModalInfoGrid>
                                    {selectedDiscount.manufacturer_name && (
                                        <ModalInfoItem>
                                            <FaBuilding size={14} />
                                            <span>{t('RetailerDistributorDetail.manufacturer')}</span>
                                            <strong>{selectedDiscount.manufacturer_name}</strong>
                                        </ModalInfoItem>
                                    )}
                                    <ModalInfoItem>
                                        <FaTruck size={14} />
                                        <span>{t('RetailerDistributorDetail.distributorLabel')}</span>
                                        <strong>{distributor?.name}</strong>
                                    </ModalInfoItem>
                                    {selectedDiscount.start_at && selectedDiscount.end_at && (
                                        <ModalInfoItem>
                                            <FaCalendarAlt size={14} />
                                            <span>{t('RetailerDistributorDetail.validPeriod')}</span>
                                            <strong>
                                                {new Intl.DateTimeFormat().format(new Date(selectedDiscount.start_at))} - {new Intl.DateTimeFormat().format(new Date(selectedDiscount.end_at))}
                                            </strong>
                                        </ModalInfoItem>
                                    )}
                                </ModalInfoGrid>
                                <ModalPriceSection>
                                    {selectedDiscount.initial_price ? (
                                        <>
                                            <ModalOldPrice>
                                                {formatResolvedAmount({
                                                    eurAmount: selectedDiscount.initial_price,
                                                    localAmount: selectedDiscount.initial_price_local,
                                                    localCurrencyId: selectedDiscount.initial_price_currency_id,
                                                })}
                                            </ModalOldPrice>
                                            <ModalNewPrice>
                                                {formatResolvedAmount({
                                                    ...resolveDiscountedAmounts(selectedDiscount),
                                                    localCurrencyId: selectedDiscount.initial_price_currency_id,
                                                })}
                                            </ModalNewPrice>
                                            <ModalSavings>
                                                {t('RetailerDistributorDetail.youSave')} {formatResolvedAmount({
                                                    eurAmount: selectedDiscount.discount_value,
                                                    localAmount: null,
                                                    localCurrencyId: selectedDiscount.initial_price_currency_id,
                                                })}
                                            </ModalSavings>
                                        </>
                                    ) : (
                                        <ModalDiscountOnlyLarge>
                                            {formatResolvedAmount({
                                                eurAmount: selectedDiscount.discount_value,
                                                localAmount: null,
                                                localCurrencyId: selectedDiscount.initial_price_currency_id,
                                            })} {t('RetailerDistributorDetail.off')}
                                        </ModalDiscountOnlyLarge>
                                    )}
                                </ModalPriceSection>
                            </ModalBody>
                        </ModalContainer>
                    </ModalOverlay>
                )}
            </PageContent>
        </PageContainer>
    );
};

export default RetailerDistributorDetail;

const truncate = (value, maxLength = 80) => {
    if (!value) return '';
    return value.length > maxLength ? `${value.slice(0, maxLength)}...` : value;
};

const BackButton = styled.button`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    background: none;
    border: none;
    color: rgba(130, 22, 146, 1);
    font-weight: 500;
    cursor: pointer;
    padding: 0;
    margin-bottom: 1.5rem;

    &:hover {
        text-decoration: underline;
    }
`;

const Header = styled.div`
    margin-bottom: 1.5rem;
`;

const HeaderInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 1rem;
`;

const DistributorLogo = styled.img`
    width: 64px;
    height: 64px;
    border-radius: 12px;
    object-fit: cover;
`;

const LogoPlaceholder = styled.div`
    width: 64px;
    height: 64px;
    border-radius: 12px;
    background: rgba(130, 22, 146, 0.1);
    color: rgba(130, 22, 146, 1);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    font-weight: 700;
`;

const HeaderText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
`;

const DistributorName = styled.h1`
    margin: 0;
    font-size: 1.5rem;
    color: #333;
`;

const DistributorDescription = styled.p`
    margin: 0;
    color: #666;
    font-size: 0.95rem;
`;

const MetricsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 1rem;
    margin-bottom: 1.5rem;
`;

const MetricCard = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 1rem;
    background: #fff;
    border: 1px solid rgba(130, 22, 146, 0.15);
    border-radius: 12px;
    text-align: center;
`;

const MetricIcon = styled.div`
    color: rgba(130, 22, 146, 0.6);
    margin-bottom: 0.5rem;
`;

const MetricValue = styled.span`
    font-size: 1.5rem;
    font-weight: 700;
    color: rgba(130, 22, 146, 1);
`;

const MetricLabel = styled.span`
    font-size: 0.875rem;
    color: #666;
`;

const TabsContainer = styled.div`
    display: flex;
    gap: 0.5rem;
    margin-bottom: 1.5rem;
    border-bottom: 1px solid rgba(130, 22, 146, 0.1);
    padding-bottom: 0.5rem;
`;

const TabButton = styled.button`
    padding: 0.6rem 1.25rem;
    border: none;
    border-radius: 8px 8px 0 0;
    background: ${({ $active }) => ($active ? 'rgba(130, 22, 146, 0.1)' : 'transparent')};
    color: ${({ $active }) => ($active ? 'rgba(130, 22, 146, 1)' : '#666')};
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background: rgba(130, 22, 146, 0.08);
    }
`;

const FiltersContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    margin-bottom: 1rem;
    align-items: center;
`;

const SearchWrapper = styled.div`
    position: relative;
    flex: 1;
    min-width: 200px;
    max-width: 350px;
`;

const SearchIcon = styled.div`
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: #888;
`;

const SearchInput = styled.input`
    width: 100%;
    padding: 0.625rem 0.875rem 0.625rem 2.375rem;
    border: 1px solid rgba(130, 22, 146, 0.3);
    border-radius: 8px;
    font-size: 0.875rem;
    outline: none;

    &:focus {
        border-color: rgba(130, 22, 146, 0.6);
        box-shadow: 0 0 0 2px rgba(130, 22, 146, 0.1);
    }
`;

const SortContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
`;

const SortLabel = styled.span`
    color: #666;
    font-size: 0.875rem;
`;

const SortButton = styled.button`
    display: flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.375rem 0.875rem;
    border-radius: 999px;
    border: 1px solid ${({ $active }) => $active ? 'rgba(130, 22, 146, 0.6)' : 'rgba(130, 22, 146, 0.2)'};
    background: ${({ $active }) => $active ? 'rgba(130, 22, 146, 0.1)' : '#fff'};
    color: ${({ $active }) => $active ? 'rgba(130, 22, 146, 1)' : '#555'};
    cursor: pointer;
    font-size: 0.8125rem;
    font-weight: 500;
    transition: all 0.2s ease;

    &:hover {
        background: rgba(130, 22, 146, 0.08);
    }
`;

const DiscountsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 1rem;
`;

const DiscountCard = styled.div`
    position: relative;
    border: 1px solid #eee;
    border-radius: 10px;
    overflow: hidden;
    background: #fff;
    transition: all 0.2s ease;
    cursor: pointer;

    &:hover {
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
        transform: translateY(-2px);
    }
`;

const DiscountBadge = styled.div`
    position: absolute;
    top: 8px;
    left: 8px;
    background: rgba(130, 22, 146, 1);
    color: #fff;
    padding: 0.25rem 0.625rem;
    border-radius: 6px;
    font-size: 0.75rem;
    font-weight: 700;
    z-index: 1;
`;

const CardImageWrapper = styled.div`
    position: relative;
    width: 100%;
    height: 100px;
    background: #f8f8f8;
`;

const ProductImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const ImagePlaceholder = styled.div`
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(130, 22, 146, 0.08) 0%, rgba(130, 22, 146, 0.04) 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.25rem;
    font-weight: 600;
    color: rgba(130, 22, 146, 0.5);
`;

const CardContent = styled.div`
    padding: 0.625rem;
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
`;

const ProductName = styled.h4`
    margin: 0;
    font-size: 0.75rem;
    font-weight: 600;
    line-height: 1.3;
    color: #333;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
`;

const ManufacturerTag = styled.span`
    font-size: 0.625rem;
    color: #666;
    background: rgba(130, 22, 146, 0.06);
    padding: 0.125rem 0.375rem;
    border-radius: 4px;
    align-self: flex-start;
`;

const CardFooter = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: auto;
    padding-top: 0.375rem;
    border-top: 1px solid #f0f0f0;
`;

const PriceSection = styled.div`
    display: flex;
    align-items: baseline;
    gap: 0.25rem;
`;

const DiscountOnly = styled.span`
    font-size: 0.75rem;
    color: rgba(130, 22, 146, 1);
    font-weight: 600;
`;

const NewPrice = styled.span`
    font-size: 0.8125rem;
    color: #2e7d32;
    font-weight: 600;
`;

const OldPrice = styled.span`
    font-size: 0.6875rem;
    color: #999;
    text-decoration: line-through;
`;

const ManufacturersGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 1rem;
`;

const ManufacturerCard = styled.div`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 1rem;
    background: #fff;
    border: 1px solid rgba(130, 22, 146, 0.12);
    border-radius: 12px;
`;

const ManufacturerLogo = styled.div`
    width: 40px;
    height: 40px;
    border-radius: 8px;
    background: rgba(130, 22, 146, 0.1);
    color: rgba(130, 22, 146, 1);
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
`;

const ManufacturerInfo = styled.div`
    display: flex;
    flex-direction: column;
`;

const ManufacturerTitle = styled.span`
    font-weight: 600;
    color: #333;
`;

const ManufacturerCategory = styled.span`
    font-size: 0.875rem;
    color: #888;
`;

const InfoSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
    max-width: 600px;
`;

const InfoItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
`;

const InfoLabel = styled.span`
    font-size: 0.875rem;
    color: #888;
    font-weight: 500;
`;

const InfoValue = styled.span`
    color: #333;

    a {
        color: rgba(130, 22, 146, 1);
        text-decoration: none;

        &:hover {
            text-decoration: underline;
        }
    }
`;

const CategoriesList = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-top: 0.25rem;
`;

const CategoryTag = styled.span`
    background: rgba(130, 22, 146, 0.1);
    color: rgba(130, 22, 146, 1);
    padding: 0.25rem 0.75rem;
    border-radius: 999px;
    font-size: 0.875rem;
`;

const LoadingPlaceholder = styled.div`
    padding: 3.75rem;
    text-align: center;
    color: #777;
`;

const Placeholder = styled.div`
    padding: 2.5rem;
    text-align: center;
    border: 1px dashed rgba(130, 22, 146, 0.3);
    border-radius: 12px;
    color: #777;
`;

const LoadingGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 1rem;
`;

const SkeletonCard = styled.div`
    border: 1px solid #eee;
    border-radius: 10px;
    overflow: hidden;
    background: #fff;
`;

const SkeletonImage = styled.div`
    width: 100%;
    height: 100px;
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: shimmer 1.5s infinite;

    @keyframes shimmer {
        0% { background-position: 200% 0; }
        100% { background-position: -200% 0; }
    }
`;

const SkeletonContent = styled.div`
    padding: 0.625rem;
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
`;

const SkeletonLine = styled.div`
    height: 14px;
    width: ${({ $width }) => $width || '100%'};
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: shimmer 1.5s infinite;
    border-radius: 4px;
`;

const Pagination = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.5rem;
    margin-top: 2rem;
    flex-wrap: wrap;
`;

const PageButton = styled.button`
    padding: 0.5rem 1rem;
    border: 1px solid rgba(130, 22, 146, 0.3);
    border-radius: 8px;
    background: #fff;
    color: ${({ disabled }) => disabled ? '#ccc' : 'rgba(130, 22, 146, 1)'};
    cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
    font-weight: 500;
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
        background: rgba(130, 22, 146, 0.05);
    }
`;

const PageNumber = styled.button`
    width: 36px;
    height: 36px;
    border: 1px solid ${({ $active }) => $active ? 'rgba(130, 22, 146, 0.8)' : 'rgba(130, 22, 146, 0.2)'};
    border-radius: 8px;
    background: ${({ $active }) => $active ? 'rgba(130, 22, 146, 1)' : '#fff'};
    color: ${({ $active }) => $active ? '#fff' : '#555'};
    cursor: pointer;
    font-weight: 500;
    transition: all 0.2s ease;

    &:hover {
        background: ${({ $active }) => $active ? 'rgba(130, 22, 146, 0.9)' : 'rgba(130, 22, 146, 0.05)'};
    }
`;

// Modal Styles
const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 1rem;
    animation: fadeIn 0.2s ease;

    @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
    }
`;

const ModalContainer = styled.div`
    background: #fff;
    border-radius: 16px;
    max-width: 500px;
    width: 100%;
    max-height: 90vh;
    overflow-y: auto;
    position: relative;
    animation: slideUp 0.25s ease;

    @keyframes slideUp {
        from {
            opacity: 0;
            transform: translateY(20px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }
`;

const ModalCloseButton = styled.button`
    position: absolute;
    top: 12px;
    right: 12px;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: none;
    background: rgba(255, 255, 255, 0.9);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1;
    transition: background 0.2s;

    &:hover {
        background: #fff;
    }
`;

const ModalImageSection = styled.div`
    position: relative;
    width: 100%;
    height: 200px;
    background: #f8f8f8;
`;

const ModalImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const ModalImagePlaceholder = styled.div`
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(130, 22, 146, 0.1) 0%, rgba(130, 22, 146, 0.05) 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 3rem;
    font-weight: 600;
    color: rgba(130, 22, 146, 0.4);
`;

const ModalDiscountBadge = styled.div`
    position: absolute;
    top: 16px;
    left: 16px;
    background: rgba(130, 22, 146, 1);
    color: #fff;
    padding: 0.5rem 1rem;
    border-radius: 8px;
    font-size: 1.125rem;
    font-weight: 700;
`;

const ModalBody = styled.div`
    padding: 1.5rem;
`;

const ModalProductName = styled.h2`
    margin: 0 0 0.75rem 0;
    font-size: 1.25rem;
    color: #333;
    line-height: 1.3;
`;

const ModalDescription = styled.p`
    color: #666;
    font-size: 0.95rem;
    line-height: 1.5;
    margin: 0 0 1rem 0;
`;

const ModalInfoGrid = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    margin-bottom: 1.5rem;
`;

const ModalInfoItem = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.875rem;

    svg {
        color: rgba(130, 22, 146, 0.6);
        flex-shrink: 0;
    }

    span {
        color: #888;
    }

    strong {
        color: #333;
        margin-left: auto;
    }
`;

const ModalPriceSection = styled.div`
    display: flex;
    align-items: baseline;
    gap: 0.75rem;
    flex-wrap: wrap;
    padding-top: 1rem;
    border-top: 1px solid #eee;
`;

const ModalOldPrice = styled.span`
    font-size: 1.1rem;
    color: #999;
    text-decoration: line-through;
`;

const ModalNewPrice = styled.span`
    font-size: 1.75rem;
    color: rgba(130, 22, 146, 1);
    font-weight: 700;
`;

const ModalSavings = styled.span`
    font-size: 0.875rem;
    color: #2e7d32;
    background: rgba(46, 125, 50, 0.1);
    padding: 0.25rem 0.625rem;
    border-radius: 4px;
`;

const ModalDiscountOnlyLarge = styled.span`
    font-size: 1.5rem;
    color: rgba(130, 22, 146, 1);
    font-weight: 700;
`;
